import { config } from '#~/config/config.js';
import { useDepot } from '#~/depot/hooks.js';
import type { State } from '#~/depot/types.js';
import { ElectronAPI } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';
import { getCurrentVendor } from '@studio/design-system/themes/vendor.js';
import type { Agent } from '@studio/models/agent';
import { useMount } from '@studio/react-utils/hooks';
import type { EqualityFn, SelectorFn } from '@studio/react-utils/store';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useRef } from 'react';
import { useState } from 'react';
import { normalizeHostname } from './discovery/utils.js';
import type { Manifest } from './types.js';

/**
 * Returns the reactive slice of all agents.
 *
 * @param selectorFn - Slice selector.
 * @param equalityFn - Slice equality function.
 * @returns Selected slice.
 */
export function useAgents<Slice>(
  selectorFn: SelectorFn<Agent[], Slice>,
  equalityFn?: EqualityFn<Slice>
): Slice {
  // Narrow the subscription to the `agents` slice. immer structurally shares
  // `µ.agents` across writes to sibling depot slices (e.g. the
  // `autoTrackingInstances` write on every proxy State Sync transaction), so on
  // such a write the cache returns the same selection reference and
  // useStoreWithEqualityFn skips the notification — unrelated writes no longer
  // wake agent consumers. A genuine agents change is a fresh reference that
  // recomputes the selection and wakes the consumer once, still gated by the
  // caller's equalityFn over the derived slice. Keying on `selectorFn` too avoids
  // returning a stale selection when a caller passes a new selector whose closure
  // changed while agents did not.
  const cache = useRef<{
    agents: State['agents'];
    selectorFn: SelectorFn<Agent[], Slice>;
    slice: Slice;
  } | null>(null);

  return useDepot((depotState) => {
    if (
      cache.current === null ||
      cache.current.agents !== depotState.agents ||
      cache.current.selectorFn !== selectorFn
    ) {
      cache.current = {
        agents: depotState.agents,
        selectorFn,
        slice: selectorFn(Object.values(depotState.agents))
      };
    }

    return cache.current.slice;
  }, equalityFn);
}

/**
 * Returns the current app version.
 */
export const useAppVersion = () => {
  const [appVersion, setAppVersion] = useState<string | null>(null);

  useMount(() => {
    if (isElectronApp()) {
      ElectronAPI.appVersion().then((version) => {
        setAppVersion(version);
      });
    }
  });

  return appVersion;
};

/**
 * Hook to fetch the installation manifest.
 *
 * @returns Installation manifest or `undefined` if the manifest is being fetched.
 * @throws `StudioError` if the manifest could not be fetched.
 */
export const useManifest = (): T_Maybe<Manifest> => {
  const [manifest, setManifest] = useState<T_Maybe<Manifest>>();
  // @ts-ignore
  const [error, setError] = useState<Error>();
  const vendor = getCurrentVendor();

  const macArmUrl = (): string => {
    switch (config.env) {
      case 'prod':
        return vendor == 'iris'
          ? 'https://dl.tryiris.ai/mac/dmg/arm64'
          : 'https://dl.hive.ptzoptics.com/mac/dmg/arm64';
      case 'stg':
        return vendor == 'iris'
          ? 'https://dl.todesktop.com/2409256e0nknca1/mac/dmg/arm64'
          : 'https://dl.todesktop.com/241114vemg5mosn/mac/dmg/arm64';
      case 'itg':
        return vendor == 'iris'
          ? 'https://dl.todesktop.com/2409256e0nknca1/mac/dmg/arm64'
          : 'https://dl.todesktop.com/241114vemg5mosn/mac/dmg/arm64';
      default:
        // Return production URL by default.
        return vendor == 'iris'
          ? 'https://dl.tryiris.ai/mac/dmg/arm64'
          : 'https://dl.hive.ptzoptics.com/mac/dmg/arm64';
    }
  };

  const win64Url = (): string => {
    switch (config.env) {
      case 'prod':
        return vendor == 'iris'
          ? 'https://dl.tryiris.ai/windows/nsis/x64'
          : 'https://dl.hive.ptzoptics.com/windows/nsis/x64';
      case 'stg':
        return vendor == 'iris'
          ? 'https://dl.todesktop.com/2409256e0nknca1/windows/nsis/x64'
          : 'https://dl.todesktop.com/241114vemg5mosn/windows/nsis/x64';
      case 'itg':
        return vendor == 'iris'
          ? 'https://dl.todesktop.com/2409256e0nknca1/windows/nsis/x64'
          : 'https://dl.todesktop.com/241114vemg5mosn/windows/nsis/x64';
      default:
        // Return production URL by default.
        return vendor == 'iris'
          ? 'https://dl.tryiris.ai/windows/nsis/x64'
          : 'https://dl.hive.ptzoptics.com/windows/nsis/x64';
    }
  };

  // Fetch the manifest on mount.
  useMount(() => {
    // @ts-ignore
    setManifest({
      macos: {
        arm64: {
          label: 'macOS (Apple Silicon)',
          fileName: 'iris-macos-arm64.zip',
          url: macArmUrl()
        }
      },
      windows: {
        win64: {
          label: 'Windows (64-bit)',
          fileName: 'iris-windows-x86.zip',
          url: win64Url()
        }
      },
      linux: {}
    });
  });

  if (error) {
    throw new StudioError('Unable to determine available software.', { cause: error });
  }

  return manifest;
};

/**
 * Custom hook to determine if the current studio is connected exclusively to embedded bridges.
 *
 * An "embedded bridge-only" studio is defined as:
 * - Having at least one connected agent/bridge
 * - ALL connected agents/bridges are embedded (iris-enabled devices)
 * - NO desktop bridges or remote connections are present
 *
 * @returns {boolean} true if all connected agents are embedded bridges, false otherwise
 */
export const useIsEmbeddedBridgeOnly = (): boolean =>
  // Boolean in the selector so a status/name write does not rebuild this
  // check. A fresh Agent[] would commit on every agents-slice write.
  useAgents((agents) => agents.length > 0 && agents.every((agent) => agent.embedded === true));

/**
 * Custom hook to determine if at least one connected agent is an embedded bridge.
 *
 * - Returns true if there is at least one agent and at least one has the "embedded" property set to true.
 * - Returns false if there are no agents or none are embedded.
 *
 * @returns {boolean} true if at least one connected agent is embedded, false otherwise
 */
export const useAtLeastOneAgentIsEmbedded = (): boolean =>
  useAgents((agents) => agents.some((agent) => agent.embedded === true));

/**
 * Custom hook to determine if all desktop bridges are offline.
 *
 * Returns `true` if:
 * - There are non-embedded agents (desktop bridges exist)
 * - AND ALL non-embedded agents are offline
 * - AND there are embedded agents (to distinguish from "no bridges at all" case)
 *
 * Returns `false` if at least one desktop bridge is online.
 *
 * This handles multi-bridge scenarios: if any desktop bridge (local machine or multi-bridge linked) is online,
 * AutoTracking should work. Only show tooltip when ALL desktop bridges are offline.
 *
 * @returns {boolean} true if all desktop bridges are offline and there are embedded agents, false otherwise
 */
export const useHasOfflineDesktopBridge = (): boolean =>
  // Compute the tooltip boolean inside the selector so a name write does
  // not wake it. Status of a desktop bridge is selected; that write still
  // commits once.
  useAgents((agents) => {
    const embeddedAgents = agents.filter((agent) => agent.embedded);
    const desktopBridges = agents.filter((agent) => !agent.embedded);

    if (desktopBridges.length === 0 || embeddedAgents.length === 0) {
      return false;
    }

    return desktopBridges.every((agent) => agent.status === 'offline');
  });

/**
 * Returns the system hostname from the local computer.
 */
export const useSystemHostname = (): T_Maybe<string> => {
  const [hostname, setHostname] = useState<string | undefined>();

  useMount(() => {
    if (!isElectronApp()) {
      return;
    }

    ElectronAPI.getSystemInfo()
      .then((systemInfo) => {
        setHostname(normalizeHostname(systemInfo?.os?.hostname));
      })
      .catch((error) => {
        console.error('Failed to get system hostname:', error);
      });
  });

  return hostname;
};

/**
 * The name the local machine goes by as the renderer sees it: undefined until
 * the IPC answers, off Electron, or when the OS reports an empty string (the
 * main-process reply type has it required).
 */
export interface MachineNamesState {
  /** The name the Bridge registers (macOS ComputerName, e.g. "Iris's Mac mini"). */
  readonly friendlyName?: string | undefined;
}

const EMPTY_MACHINE_NAMES: MachineNamesState = {};

/**
 * The machine name for the Bridge Setup picker. Unlike useSystemHostname this
 * reads a dedicated IPC backed by os/scutil, so it is not emptied by a wedged
 * systeminformation probe and is never run through the mDNS remote-host
 * humanizer.
 */
export const useMachineNames = (): MachineNamesState => {
  const [names, setNames] = useState<MachineNamesState>(EMPTY_MACHINE_NAMES);

  useMount(() => {
    if (!isElectronApp()) {
      return;
    }

    ElectronAPI.getMachineNames()
      .then((reply) => {
        if (reply === undefined) return;
        setNames({ friendlyName: reply.friendlyName.trim() || undefined });
      })
      .catch((error: unknown) => {
        // Per rule: use-canonical-logger-not-console (ai.knowledge/global).
        logger.warning(`[machine-names] system:machine-name IPC failed: ${String(error)}`);
      });
  });

  return names;
};
